export type MonthlyMode = 'day_of_month' | 'day_of_week';

export interface RuleValidations {
  day?: number[];
  day_of_month?: number[];
  day_of_week?: Record<string, number[]>;
}

export interface RuleHash {
  rule_type: string;
  interval: number;
  week_start?: number;
  validations?: RuleValidations;
}

export interface RuleInput {
  frequency: string;
  interval: number;
  weekStart: number;
  days: number[];
  daysOfMonth: number[];
  weeks: Record<string, number[]>;
  monthlyMode: MonthlyMode;
}

const RULE_TYPE_BY_FREQUENCY: Record<string, string> = {
  Daily: 'IceCube::DailyRule',
  Weekly: 'IceCube::WeeklyRule',
  Monthly: 'IceCube::MonthlyRule',
  Yearly: 'IceCube::YearlyRule',
};

const FREQUENCY_BY_RULE_TYPE: Record<string, string> = {
  'IceCube::DailyRule': 'Daily',
  'IceCube::WeeklyRule': 'Weekly',
  'IceCube::MonthlyRule': 'Monthly',
  'IceCube::YearlyRule': 'Yearly',
};

export function buildRule(input: RuleInput): RuleHash {
  const { frequency, monthlyMode } = input;
  const rule: RuleHash = {
    rule_type: RULE_TYPE_BY_FREQUENCY[frequency] ?? 'IceCube::WeeklyRule',
    interval: Math.max(1, Math.trunc(input.interval) || 1),
  };

  if (frequency === 'Weekly') {
    rule.week_start = input.weekStart || 0;
    rule.validations = { day: sortedNumbers(input.days) };
  } else if (frequency === 'Monthly') {
    rule.validations =
      monthlyMode === 'day_of_week'
        ? { day_of_month: [], day_of_week: sortedWeeks(input.weeks) }
        : { day_of_month: sortedNumbers(input.daysOfMonth), day_of_week: {} };
  } else {
    rule.validations = {};
  }

  return rule;
}

export function frequencyForRule(rule: RuleHash | null): string {
  if (!rule) return 'Weekly';

  return FREQUENCY_BY_RULE_TYPE[rule.rule_type] ?? 'Weekly';
}

export function parseRule(value: string | null | undefined): RuleHash | null {
  if (!value || value === 'null' || value === 'custom') return null;

  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as RuleHash) : null;
  } catch {
    return null;
  }
}

function sortedNumbers(values: number[]): number[] {
  return [...values].map(Number).sort((a, b) => a - b);
}

function sortedWeeks(weeks: Record<string, number[]>): Record<string, number[]> {
  return Object.fromEntries(
    Object.entries(weeks).map(([day, ordinals]) => [day, sortedNumbers(ordinals)]),
  );
}
